import { client } from './api/client.gen';
import type { ResolvedRequestOptions } from './api/client/types.gen';
import { useAuthStore } from '@/stores/authStore';
import { tokenRefreshManager } from '@/utils/tokenRefresh';

// Configure the OpenAPI client
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';

console.log('[ApiClient] Configuring OpenAPI client');

client.setConfig({
  baseUrl: API_BASE_URL,
});

/**
 * Shared token resolver — used by both SDK interceptors and direct-fetch services
 * (agentProxyService, aiAssistantService, monitoringService)
 */
export async function getApiToken(): Promise<string> {
  const storeToken = useAuthStore.getState().token;
  const accessToken = localStorage.getItem('access_token');
  const localToken = localStorage.getItem('token');
  return storeToken || accessToken || localToken || '';
}

/**
 * Shared base URL accessor — used by direct-fetch services
 */
export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

// Request interceptor: always attach latest token
// oxlint-disable-next-line no-async-endpoint-handlers -- False positive: this is an HTTP client interceptor, not an Express endpoint handler
client.interceptors.request.use(async (request) => {
  const token = await getApiToken();
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }
  return request;
});

// Single-flight refresh: only one refresh at a time across all requests
let refreshPromise: Promise<void> | null = null;

export async function refreshTokenSingleFlight(): Promise<void> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      await tokenRefreshManager.executeRefresh();
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Track retried requests to prevent infinite retry loops
const retriedRequests = new WeakSet<Request>();

// Response interceptor: handle TOKEN_EXPIRED with retry
client.interceptors.response.use(async (response: Response, request: Request, opts: ResolvedRequestOptions) => {
  if (response.status === 401) {
    const body = await response.clone().json().catch(() => null) as Record<string, unknown> | null;
    if (body?.error === 'TOKEN_EXPIRED') {
      // Prevent infinite retry: if this request was already retried, clear auth
      if (retriedRequests.has(request)) {
        console.error('[ApiClient] Retried request still returned 401, clearing auth');
        const { clearAuth } = useAuthStore.getState();
        clearAuth();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return response;
      }

      console.log('[ApiClient] TOKEN_EXPIRED detected, refreshing token...');
      try {
        await refreshTokenSingleFlight();
        console.log('[ApiClient] Token refreshed, retrying original request...');

        // Retry: reconstruct request with new token
        const newToken = await getApiToken();
        const headers = new Headers(request.headers);
        headers.set('Authorization', `Bearer ${newToken}`);

        const retryRequest = new Request(request.url, {
          method: request.method,
          headers,
          body: opts.serializedBody ?? undefined,
        });

        // Mark the retry request to prevent infinite loops
        retriedRequests.add(retryRequest);

        const _fetch = opts.fetch ?? globalThis.fetch;
        return await _fetch(retryRequest);
      } catch {
        console.error('[ApiClient] Token refresh failed, clearing auth');
        const { clearAuth } = useAuthStore.getState();
        clearAuth();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
  }
  return response;
});

/**
 * Authenticated fetch wrapper for direct-fetch services (agentProxyService, monitoringService, aiAssistantService).
 * Attaches the current token, and on TOKEN_EXPIRED 401 refreshes and retries once.
 */
export async function authenticatedFetch(
  url: string,
  init?: RequestInit
): Promise<Response> {
  const token = await getApiToken();
  const headers = new Headers(init?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let response = await fetch(url, { ...init, headers });

  if (response.status === 401) {
    // Try to parse error body — use .catch() since response may not be JSON (e.g., SSE streams)
    const body = await response.clone().json().catch(() => null) as Record<string, unknown> | null;
    if (body?.error === 'TOKEN_EXPIRED') {
      console.log('[authenticatedFetch] TOKEN_EXPIRED detected, refreshing and retrying...');
      try {
        await refreshTokenSingleFlight();
        const newToken = await getApiToken();
        headers.set('Authorization', `Bearer ${newToken}`);
        response = await fetch(url, { ...init, headers });
      } catch {
        console.error('[authenticatedFetch] Token refresh failed, clearing auth');
        const { clearAuth } = useAuthStore.getState();
        clearAuth();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
  }

  return response;
}

console.log('[ApiClient] OpenAPI client configured');
console.log('[ApiClient] TOKEN_EXPIRED handling is active via response interceptor with retry');

// Re-export generated API (types and SDK functions)
export * from './api';
export { client } from './api/client.gen';
